// Iter117.2 — Shared freshness indicator + refresh button.
//
// Extracted from MarketForge.js so future tables (Cycle Tracker,
// Performance, Decision Journal, etc.) can drop in the same
// "🟢 7s ago · REFRESH" UX with one line. Keeps the cockpit's
// "is this data fresh?" mental model consistent across the app.
//
// Pure presentational — accepts `fetchedAt` (timestamp ms or null)
// and `onRefresh` (callback). Internally ticks every 10s so the
// `Xs/Xm/Xh ago` label stays accurate without re-mounting.

import React, { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";

export const FRESHNESS_COLORS = {
  fresh:   "#00FF88",  // <60s
  ageing:  "#FFB800",  // 60s–5min
  stale:   "#FF3366",  // >5min
  unknown: "#5F5F66",  // never fetched
};

function colorForAge(seconds) {
  if (seconds == null) return FRESHNESS_COLORS.unknown;
  if (seconds < 60) return FRESHNESS_COLORS.fresh;
  if (seconds < 300) return FRESHNESS_COLORS.ageing;
  return FRESHNESS_COLORS.stale;
}

function labelForAge(seconds) {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}

/**
 * FreshnessIndicator — shows a coloured-dot + ago-label + Refresh button.
 *
 * Props:
 *   fetchedAt    — number (ms) timestamp of last successful fetch, or null
 *   onRefresh    — () => void, called when the operator clicks Refresh
 *   loading      — boolean, true while a refresh is in flight
 *   testidPrefix — string, used to derive `${prefix}-freshness` + `${prefix}-refresh`
 *   refreshTitle — string, tooltip shown on the Refresh button
 *   tickMs       — number, optional override for the wall-clock tick (default 10000)
 */
export default function FreshnessIndicator({
  fetchedAt,
  onRefresh,
  loading = false,
  testidPrefix,
  refreshTitle = "Refresh",
  tickMs = 10000,
}) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), tickMs);
    return () => window.clearInterval(id);
  }, [tickMs]);

  const ageSeconds = fetchedAt ? Math.max(0, Math.floor((now - fetchedAt) / 1000)) : null;
  const color = colorForAge(ageSeconds);
  const label = labelForAge(ageSeconds);

  return (
    <div className="flex items-center gap-2">
      <div
        data-testid={`${testidPrefix}-freshness`}
        className="flex items-center gap-1.5 text-[9px] font-mono"
        title={fetchedAt ? `Last fetched at ${new Date(fetchedAt).toLocaleTimeString()}` : "Not yet fetched"}
      >
        <span
          className="inline-block w-1.5 h-1.5 rounded-full"
          style={{ backgroundColor: color, boxShadow: `0 0 4px ${color}80` }}
        />
        <span className="text-[#8A8A93]">{label}</span>
      </div>
      <button
        type="button"
        data-testid={`${testidPrefix}-refresh`}
        onClick={(e) => {
          // Defensive: stop bubbling so the refresh button can be embedded
          // inside larger clickable surfaces (e.g. a card that navigates on
          // click) without accidentally triggering the parent action.
          e.stopPropagation();
          if (onRefresh) onRefresh();
        }}
        disabled={loading}
        title={refreshTitle}
        className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.15em] px-2 py-1 rounded-sm border bg-[#0A0A0E] text-[#8A8A93] border-[#26262B] hover:border-[#00FF88]/40 hover:text-[#00FF88] disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <RefreshCw size={10} className={loading ? "animate-spin" : ""} />
        Refresh
      </button>
    </div>
  );
}
